/**
 * DeviceProvisioningPage — `/franchise/devices`.
 *
 * Backed by the dedicated `/v1/franchise/devices` surface. Multiple devices
 * per franchise; statuses ACTIVE / DECOMMISSIONED / MAINTENANCE / LOST; per
 * device heartbeat + decommission audit.
 */
import { type JSX, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  createDevice,
  decommissionDevice,
  type DeviceStatus,
  type FranchiseDevice,
  heartbeatDevice,
  listDevices,
  listFranchises,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { FilterBar } from '../mail/shared/MailUI';

const STATUSES: readonly DeviceStatus[] = ['ACTIVE', 'DECOMMISSIONED', 'MAINTENANCE', 'LOST'];

export function DeviceProvisioningPage(): JSX.Element {
  const qc = useQueryClient();
  const [franchiseId, setFranchiseId] = useState('');
  const [serial, setSerial] = useState('');
  const [model, setModel] = useState('');
  const [posTerminal, setPosTerminal] = useState('');
  const [statusFilter, setStatusFilter] = useState<DeviceStatus | ''>('');
  const [search, setSearch] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const franchises = useQuery({
    queryKey: ['franchise', 'devices', 'franchises'],
    queryFn: () => listFranchises({ pageSize: 100 }),
    retry: false,
  });

  const devices = useQuery({
    queryKey: ['franchise', 'devices', 'list', statusFilter],
    queryFn: () =>
      listDevices(statusFilter === '' ? { pageSize: 200 } : { pageSize: 200, status: statusFilter }),
    retry: false,
  });

  const denied =
    (franchises.isError && franchises.error instanceof ApiError &&
      (franchises.error.status === 401 || franchises.error.status === 403)) ||
    (devices.isError && devices.error instanceof ApiError &&
      (devices.error.status === 401 || devices.error.status === 403));

  const franchiseIndex = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of franchises.data?.items ?? []) {
      map.set(f.id, `${f.legalName} (${f.franchiseNumber})`);
    }
    return map;
  }, [franchises.data]);

  const filteredDevices = useMemo(() => {
    const all = devices.data?.items ?? [];
    if (search.trim().length === 0) return all;
    const q = search.toLowerCase();
    return all.filter(
      (d) =>
        d.serial.toLowerCase().includes(q) ||
        (d.model ?? '').toLowerCase().includes(q) ||
        (franchiseIndex.get(d.franchiseId) ?? '').toLowerCase().includes(q),
    );
  }, [devices.data, franchiseIndex, search]);

  const provision = useMutation({
    mutationFn: () =>
      createDevice({
        franchiseId,
        serial: serial.trim(),
        model: model.trim() === '' ? undefined : model.trim(),
        posTerminalId: posTerminal.trim() === '' ? undefined : posTerminal.trim(),
      }),
    onSuccess: () => {
      setSubmitted(true);
      setErr(null);
      setSerial('');
      setModel('');
      setPosTerminal('');
      void qc.invalidateQueries({ queryKey: ['franchise', 'devices'] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  const decommission = useMutation({
    mutationFn: (id: string) => decommissionDevice(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['franchise', 'devices'] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  const heartbeat = useMutation({
    mutationFn: (id: string) => heartbeatDevice(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['franchise', 'devices'] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Device provisioning"
        description="Counter device fleet — provision, heartbeat, decommission."
      />

      <section className="bg-white border border-border rounded-xl p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-900">New provisioning</h2>

        {franchises.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : denied ? (
          <DeferredNotice title="Permission required">
            <code className="font-mono">FranchiseDevice/write</code> required.
          </DeferredNotice>
        ) : franchises.isError ? (
          <DeferredNotice title="Service unreachable">
            {(franchises.error as Error).message}
          </DeferredNotice>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Franchise</label>
              <select
                value={franchiseId}
                onChange={(e) => {
                  setFranchiseId(e.target.value);
                  setSubmitted(false);
                }}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                data-testid="device-franchise-select"
              >
                <option value="">Select…</option>
                {franchises.data?.items.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.legalName} ({f.franchiseNumber}) · {f.status}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Device serial</label>
              <input
                type="text"
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
                data-testid="device-serial-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Model</label>
              <input
                type="text"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">POS terminal id</label>
              <input
                type="text"
                value={posTerminal}
                onChange={(e) => setPosTerminal(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
              />
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => {
            setSubmitted(false);
            setErr(null);
            provision.mutate();
          }}
          disabled={
            franchiseId.length === 0 ||
            serial.trim().length === 0 ||
            provision.isPending
          }
          className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          data-testid="device-provision-btn"
        >
          {provision.isPending ? 'Provisioning…' : 'Provision device'}
        </button>

        {submitted ? (
          <div className="flex items-center gap-2 text-xs">
            <StatusBadge status="ACCEPTED" />
            <span className="text-muted">Device persisted.</span>
          </div>
        ) : null}
        {err ? <p className="text-xs text-danger">{err}</p> : null}
      </section>

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Filter by serial, model or franchise…"
      >
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as DeviceStatus | '')}
          className="px-3 py-2 border border-border rounded-lg text-sm"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </FilterBar>

      {devices.isPending ? (
        <p className="text-sm text-muted">Loading devices…</p>
      ) : devices.isError ? (
        <DeferredNotice title="Service unreachable">{(devices.error as Error).message}</DeferredNotice>
      ) : filteredDevices.length === 0 ? (
        <p
          className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center"
          data-testid="device-empty"
        >
          No devices match the filter.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">Serial</th>
                <th className="text-left px-4 py-2">Franchise</th>
                <th className="text-left px-4 py-2">Model / POS</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Provisioned</th>
                <th className="text-left px-4 py-2">Last seen</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredDevices.map((d: FranchiseDevice) => (
                <tr key={d.id} className="hover:bg-gray-50" data-testid="device-row">
                  <td className="px-4 py-2 font-mono text-xs">{d.serial}</td>
                  <td className="px-4 py-2 text-xs text-gray-700">
                    {franchiseIndex.get(d.franchiseId) ?? d.franchiseId}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {d.model ?? '—'} {d.posTerminalId ? `· ${d.posTerminalId}` : ''}
                  </td>
                  <td className="px-4 py-2"><StatusBadge status={d.status} /></td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {new Date(d.provisionedAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {d.lastSeenAt ? new Date(d.lastSeenAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-2 text-right space-x-2">
                    {d.status === 'ACTIVE' ? (
                      <>
                        <button
                          type="button"
                          onClick={() => heartbeat.mutate(d.id)}
                          disabled={heartbeat.isPending}
                          className="text-xs font-semibold text-nipost-green hover:underline disabled:opacity-50"
                        >
                          Heartbeat
                        </button>
                        <button
                          type="button"
                          onClick={() => decommission.mutate(d.id)}
                          disabled={decommission.isPending}
                          className="text-xs font-semibold text-danger hover:underline disabled:opacity-50"
                        >
                          Decommission
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
